import Chart, { YAxis, formatAmount, niceMax, useBoxWidth, withUnit, xLabelIndexes } from './Chart';

// §2.6 (rev 2.3) DayBars: one column per day (or week) with the target as an ink-2 rule across each column's slot, so a
// day off or a changed target shows as a step, not a false straight line. Columns under their target at half strength.
// Only the latest column carries its number; every column has a title tooltip.
//   points: [{ key, label, amount, target }]; upTo: the index of the last point that has happened; unit 'words' | 'min'.
//   compact: the small-multiple size (no y labels, 120 px tall, bars only, shared `max`).
export function Bars({ points, upTo = points.length - 1, unit, W, H, pl, pr, pt, pb, max, labels, compact = false, label }) {
  const n = points.length;
  const slot = (W - pl - pr) / Math.max(n, 1);
  const bw = Math.max(1, Math.min(compact ? 20 : 24, slot * 0.6));
  const x = i => pl + slot * (i + 0.5);
  const y = v => pt + (H - pt - pb) * (1 - Math.min(v, max) / max);
  const lastShown = Math.min(upTo, n - 1);
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
      <YAxis max={max} unit={unit} x0={pl} x1={W - pr} y={y} show={!compact} />
      {points.map((p, i) => {
        if (i > lastShown) return null;
        const under = p.target > 0 && (p.amount || 0) < p.target;
        const h = y(0) - y(p.amount || 0);
        return (
          <g key={p.key}>
            {h > 0 && (
              <rect className={`ui-mark${under ? ' ui-mark--under' : ''}`} x={x(i) - bw / 2} y={y(p.amount)} width={bw} height={h} rx={bw >= 6 ? 2 : 0} />
            )}
            <rect className="ui-chart__hit" x={x(i) - slot / 2} y={pt} width={slot} height={H - pt - pb}>
              <title>{`${p.label}: ${withUnit(p.amount || 0, unit)}${p.target > 0 ? ` of ${formatAmount(p.target, unit)}` : ''}`}</title>
            </rect>
          </g>
        );
      })}
      {points.map((p, i) => p.target > 0 && (
        <line key={`t${p.key}`} className="ui-chart__target" x1={x(i) - slot * 0.45} x2={x(i) + slot * 0.45} y1={y(p.target)} y2={y(p.target)} />
      ))}
      {points.map((p, i) => {
        if (!labels.has(i)) return null;
        // A small chart's first and last labels sit inside its own box, never over the next chart.
        const edge = compact && n > 7 ? (i === 0 ? 'start' : i === n - 1 ? 'end' : null) : null;
        const lx = edge === 'start' ? pl : edge === 'end' ? W - pr : x(i);
        return <text key={`x${p.key}`} className="ui-chart__axis" x={lx} y={H - (compact ? 6 : 8)} textAnchor={edge || 'middle'}>{p.axis || p.label}</text>;
      })}
      {!compact && lastShown >= 0 && (points[lastShown].amount || 0) > 0 && (
        <text className="ui-chart__lab ui-chart__lab--strong" x={x(lastShown)} y={y(points[lastShown].amount) - 6} textAnchor="middle">{formatAmount(points[lastShown].amount, unit)}</text>
      )}
    </svg>
  );
}

export default function DayBars({ title, note, points, upTo = points.length - 1, unit = 'min', periodLabel = 'Day' }) {
  const [ref, w] = useBoxWidth(560);
  const W = Math.max(w, 280), H = 220, pl = 40, pr = 16, pt = 16, pb = 28;
  const shown = points.slice(0, Math.min(upTo, points.length - 1) + 1);
  const max = niceMax(Math.max(0, ...points.map(p => Math.max(p.amount || 0, p.target || 0))));
  const total = shown.reduce((s, p) => s + (p.amount || 0), 0);
  const table = {
    columns: [
      { key: 'label', label: periodLabel },
      { key: 'amount', label: 'Approved', align: 'right', render: r => <span className="ui-num">{formatAmount(r.amount || 0, unit)}</span> },
      { key: 'target', label: 'Target', align: 'right', render: r => (r.target > 0 ? <span className="ui-num">{formatAmount(r.target, unit)}</span> : <span className="ui-text-3">None</span>) },
    ],
    rows: shown, rowKey: r => r.key,
  };
  return (
    <Chart title={title} note={note} table={table}>
      <div className="ui-chart__plot" ref={ref}>
        <Bars points={points} upTo={upTo} unit={unit} W={W} H={H} pl={pl} pr={pr} pt={pt} pb={pb} max={max} labels={xLabelIndexes(points.length, W - pl - pr)}
          label={`${title}: ${withUnit(total, unit)} in ${shown.length} ${shown.length === 1 ? 'column' : 'columns'}.`} />
      </div>
    </Chart>
  );
}
